import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { verifyEmail } from "../../api/verifyEmail";
import { useDebounce } from "../../hooks/useDebounce";
import { VALIDATION_RULES, validateForm } from "../../utils/formValidation";
import { Button } from "../common/Button";
import { FormInput, FormLayout } from "../common/FormLayout";

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Utilisation du debounce sur l'email
  const debouncedEmail = useDebounce(formData.email, 800);

  // Vérification de l'email après le debounce
  useEffect(() => {
    const checkEmail = async () => {
      if (debouncedEmail && debouncedEmail.length > 0) {
        setIsCheckingEmail(true);
        const { isValid, message } = await verifyEmail(debouncedEmail);

        if (!isValid && message) {
          setErrors((prev) => ({ ...prev, email: t(message) }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.email;
            return newErrors;
          });
        }
        setIsCheckingEmail(false);
      }
    };

    checkEmail();
  }, [debouncedEmail, t]);

  const getValidationRules = () => ({
    nom: [
      VALIDATION_RULES.required(t("register-error-name")),
      VALIDATION_RULES.minLength(2),
      VALIDATION_RULES.onlyLetters(),
    ],
    prenom: [
      VALIDATION_RULES.required(t("register-error-firstname")),
      VALIDATION_RULES.minLength(2),
      VALIDATION_RULES.onlyLetters(),
    ],
    email: [
      VALIDATION_RULES.required(t("register-error-email")),
      VALIDATION_RULES.email(),
    ],
    telephone: [
      VALIDATION_RULES.required(t("register-error-phone")),
      VALIDATION_RULES.phone(),
    ],
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationRules = getValidationRules();
    const validationErrors = validateForm(formData, validationRules);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    navigate("/register/company", { state: { formData } });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Effacer l'erreur du champ modifié sauf pour l'email qui est géré par le debounce
    if (errors[name] && name !== "email") {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <FormLayout title={t("register-title")}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormInput
          label={t("register-lastname")}
          type="text"
          name="nom"
          value={formData.nom}
          onChange={handleChange}
          required
          error={errors.nom}
        />

        <FormInput
          label={t("register-firstname")}
          type="text"
          name="prenom"
          value={formData.prenom}
          onChange={handleChange}
          required
          error={errors.prenom}
        />

        <div className="relative">
          <FormInput
            label={t("login-email")}
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            error={errors.email}
          />
          {isCheckingEmail && (
            <div className="absolute right-3 top-9">
              <FontAwesomeIcon
                icon="sync"
                className="text-grey-medium animate-spin h-5 w-5"
              />
            </div>
          )}
        </div>

        <FormInput
          label={t("register-phone")}
          type="tel"
          name="telephone"
          value={formData.telephone}
          onChange={handleChange}
          required
          error={errors.telephone}
        />

        <div className="flex gap-4">
          <Button
            type="button"
            variant="secondary"
            size="full"
            onClick={() => navigate("/login")}
          >
            {t("register-back")}
          </Button>
          <Button
            type="submit"
            size="full"
            disabled={isCheckingEmail || Object.keys(errors).length > 0}
          >
            {t("register-next")}
          </Button>
        </div>

        <div className="text-center mt-4">
          <a href="/login" className="text-tertiary hover:underline">
            {t("register-have-account")}
          </a>
        </div>
      </form>
    </FormLayout>
  );
};
